import React, { useState, useEffect } from 'react';
import { withStyles } from '@mui/styles';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  IconButton,
  Typography,
  CircularProgress,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import DocumentMSStyle from './DocumentMSStyle';
import { updateAssignmentStatusByRecordId } from './dmsApi';

const SendPackageDialog = (props) => {
  const { classes, open, onClose, document, assignment, onSuccess } = props;
  const navigate = useNavigate();
  const [packageMethod, setPackageMethod] = useState('sent_via_jiffy');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Set initial package method based on assignment's current status
  useEffect(() => {
    if (open && assignment) {
      const currentStatus = assignment.current_status;
      // Check if current status matches one of the package methods
      if (currentStatus === 'sent_via_jiffy' || 
          currentStatus === 'sent_via_other') {
        setPackageMethod(currentStatus);
      } else {
        setPackageMethod('sent_via_jiffy'); // Default
      }
      // Set notes from assignment if available
      if (assignment.notes) {
        setNotes(assignment.notes);
      }
    } else if (open && !assignment) {
      // Reset to default if no assignment
      setPackageMethod('sent_via_jiffy');
      setNotes('');
    }
  }, [open, assignment]);

  const handleClose = () => {
    if (!loading) {
      setPackageMethod('sent_via_jiffy');
      setNotes('');
      setError(null);
      onClose();
    }
  };

  const handleSend = async () => {
    // If sent_via_jiffy, just redirect without API call
    if (packageMethod === 'sent_via_jiffy') {
      handleClose();
      navigate('/sendpackage');
      return;
    }

    // For other methods, update status via API
    if (!assignment || !assignment.record_id) {
      setError('Assignment information is missing');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      // Update assignment status using record_id
      await updateAssignmentStatusByRecordId(
        assignment.record_id,
        packageMethod,
        notes || '',
        null
      );

      // Call success callback if provided
      if (onSuccess) {
        onSuccess({
          packageMethod,
          notes,
        });
      }

      // Close dialog
      handleClose();
      // If packageMethod is 'sent_via_other', no navigation needed
    } catch (err) {
      console.error('Failed to update assignment status:', err);
      setError(err.detail || err.error || 'Failed to update status. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      PaperProps={{
        style: {
          borderRadius: '16px',
          fontFamily: 'Montserrat',
        },
      }}
    >
      <DialogTitle 
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '24px 24px 16px 24px',
          fontSize: '20px',
          fontWeight: 600,
          color: '#131C4C',
          fontFamily: 'Montserrat',
        }}
      >
        Send Document Package
        <IconButton
          onClick={handleClose}
          size="small"
          style={{ color: '#6E759A' }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent style={{ padding: '0 24px 24px 24px', position: 'relative' }}>
        {loading && (
          <Box
            style={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(255, 255, 255, 0.95)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
              borderRadius: '0 0 16px 16px',
            }}
          >
            <Box style={{ textAlign: 'center' }}>
              <CircularProgress style={{ color: '#0F1638', marginBottom: '16px' }} />
              <Typography style={{ fontSize: '14px', color: '#131C4C', fontFamily: 'Montserrat', fontWeight: 500 }}>
                Updating status...
              </Typography>
            </Box>
          </Box>
        )}
        <Box style={{ marginBottom: '24px', fontSize: '14px', color: '#6E759A', fontFamily: 'Montserrat' }}>
          Send this document package for processing: <strong style={{ color: '#131C4C' }}>{document?.document_name || 'Document'}</strong>
        </Box>
        
        {error && (
          <Box
            className={classes.error}
            style={{
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#FFEBEE',
              color: '#C62828',
              borderRadius: '8px',
              fontSize: '14px',
              fontFamily: 'Montserrat',
            }}
          >
            {error}
          </Box>
        )}
        
        <FormControl component="fieldset" style={{ width: '100%', marginBottom: '24px' }}>
          <FormLabel 
            component="legend" 
            style={{ 
              fontSize: '14px', 
              fontWeight: 600, 
              color: '#131C4C', 
              fontFamily: 'Montserrat',
              marginBottom: '12px'
            }}
          >
            Package Method
          </FormLabel>
          <RadioGroup
            value={packageMethod}
            onChange={(e) => setPackageMethod(e.target.value)}
            style={{ gap: '12px' }}
          >
            <FormControlLabel
              value="sent_via_jiffy"
              control={
                <Radio 
                  style={{ color: '#131C4C' }}
                  sx={{
                    '&.Mui-checked': {
                      color: '#131C4C',
                    },
                  }}
                />
              }
              label={
                <Typography style={{ fontFamily: 'Montserrat', fontSize: '14px', color: '#131C4C' }}>
                  Document send through Jiffy 
                </Typography>
              }
            />
            <FormControlLabel
              value="sent_via_other"
              control={
                <Radio 
                  style={{ color: '#131C4C' }}
                  sx={{
                    '&.Mui-checked': {
                      color: '#131C4C',
                    },
                  }}
                />
              }
              label={
                <Typography style={{ fontFamily: 'Montserrat', fontSize: '14px', color: '#131C4C' }}>
                  Document send via other 
                </Typography>
              }
            />
          </RadioGroup>
        </FormControl>
        
        <Box className={classes.formGroup}>
          <Box className={classes.label} style={{ marginBottom: '8px', fontSize: '14px', fontWeight: 600, color: '#131C4C', fontFamily: 'Montserrat' }}>
            Additional Notes (Optional)
          </Box>
          <TextField
            className={classes.input}
            fullWidth
            multiline
            rows={4}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add any notes or instructions...."
            style={{ fontFamily: 'Montserrat' }}
            sx={{
              '& .MuiOutlinedInput-root': {
                fontFamily: 'Montserrat',
                fontSize: '14px',
              },
            }}
          />
        </Box>
      </DialogContent>
      <DialogActions style={{ padding: '16px 24px 24px 24px', gap: '12px' }}>
        <Button 
          onClick={handleClose}
          className={classes.buttonSecondary}
          disabled={loading}
          style={{
            textTransform: 'none',
            fontFamily: 'Montserrat',
            fontSize: '14px',
            fontWeight: 500,
            padding: '8px 24px',
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSend}
          className={classes.button}
          variant="contained"
          disabled={loading || !assignment}
          startIcon={loading ? <CircularProgress size={16} style={{ color: '#FFFFFF' }} /> : <SendIcon style={{ fontSize: '18px' }} />}
          style={{
            textTransform: 'none',
            backgroundColor: '#0F1638',
            color: '#FFFFFF',
            fontFamily: 'Montserrat',
            fontSize: '14px',
            fontWeight: 500,
            padding: '8px 24px',
            '&:hover': {
              backgroundColor: '#0A0F25',
            },
          }}
        >
          {loading ? 'Updating...' : 'Send Package'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default withStyles(DocumentMSStyle)(SendPackageDialog);

